import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import Logo from './Logo';
import SocialLinks from './SocialLinks';
import { useSiteTexts } from '../lib/siteTexts';

const Footer = () => {
    const location = useLocation();
    const t = useSiteTexts();
    if (location.pathname.startsWith('/admin')) return null;

    return (
        <footer
            className="relative pt-24 pb-10 bg-[#050505] border-t border-white/10 overflow-hidden"
            data-testid="site-footer"
        >
            <div className="container-page grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10">
                <div className="md:col-span-2 space-y-4">
                    <Logo size="md" plain />
                    <p className="text-white/60 max-w-md text-sm leading-relaxed whitespace-pre-line" data-testid="footer-about">
                        {t('footer.about')}
                    </p>
                    <div className="flex flex-col gap-2 text-sm text-white/70">
                        <a href={`tel:${t('footer.phone').replace(/[^\d+]/g, '')}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-phone">
                            <Phone className="h-4 w-4 text-brand-primary" /> {t('footer.phone')}
                        </a>
                        <a href={`mailto:${t('footer.email')}`} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-email">
                            <Mail className="h-4 w-4 text-brand-primary" /> {t('footer.email')}
                        </a>
                        <span className="flex items-center gap-2" data-testid="footer-location">
                            <MapPin className="h-4 w-4 text-brand-primary" /> {t('footer.location')}
                        </span>
                    </div>

                    <div className="pt-3">
                        <div className="label-caps text-white/40 mb-3">Follow us</div>
                        <SocialLinks />
                    </div>
                </div>

                <div>
                    <h4 className="label-caps text-white/50 mb-4">Shop</h4>
                    <ul className="space-y-2 text-sm text-white/70">
                        <li><Link to="/shop?type=machine" className="hover:text-white transition-colors">Dry Ice Machines</Link></li>
                        <li><Link to="/shop?type=accessory" className="hover:text-white transition-colors">Accessories & Parts</Link></li>
                        <li><Link to="/shop?category=used" className="hover:text-white transition-colors">Used Equipment</Link></li>
                        <li><Link to="/cart" className="hover:text-white transition-colors">Cart</Link></li>
                        <li><Link to="/rental" className="hover:text-white transition-colors" data-testid="footer-rental">Rental</Link></li>
                        <li><Link to="/training" className="hover:text-white transition-colors" data-testid="footer-training">Training</Link></li>
                    </ul>
                </div>

                <div>
                    <h4 className="label-caps text-white/50 mb-4">Company</h4>
                    <ul className="space-y-2 text-sm text-white/70">
                        <li><Link to="/services" className="hover:text-white transition-colors">Service & Support</Link></li>
                        <li><Link to="/partners" className="hover:text-white transition-colors">Partners</Link></li>
                        <li><Link to="/videos" className="hover:text-white transition-colors">Videos</Link></li>
                        <li><Link to="/news" className="hover:text-white transition-colors">News</Link></li>
                        <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
                        <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                    </ul>
                </div>
            </div>

            <div className="pointer-events-none absolute left-0 right-0 -bottom-8 md:-bottom-16 lg:-bottom-24 text-center">
                <div className="font-display font-black tracking-tighter text-[18vw] leading-none text-white/[0.04] select-none whitespace-nowrap">
                    DRY ICE BULLS®
                </div>
            </div>

            <div className="container-page mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40 relative z-10">
                <span data-testid="footer-copyright">{t('footer.copyright')}</span>
                <div className="flex flex-wrap items-center gap-3 label-caps">
                    <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
                    <span className="text-white/20">·</span>
                    <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
                    <span className="text-white/20">·</span>
                    <Link to="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies">Cookies</Link>
                    <span className="text-white/20">·</span>
                    <Link to="/disclaimer" className="hover:text-white transition-colors" data-testid="footer-disclaimer">Disclaimer</Link>
                    <span className="text-white/20">·</span>
                    <span>B2B only</span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
